import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Bell, MessageSquare, Menu, X, Globe, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";
import { useI18n, LANGS } from "../i18n";
import { api } from "../lib/api";
import { Avatar, LOGO } from "./bits";

export function LangSwitcher() {
  const { lang, setLang } = useI18n();
  const { user } = useAuth();
  const change = (l) => { setLang(l); if (user) api.put("/auth/preferences", { ui_lang: l }).catch(() => {}); };
  return (
    <label className="relative inline-flex items-center gap-1 text-sm">
      <Globe size={16} className="text-slate-500" aria-hidden />
      <span className="sr-only">Language</span>
      <select value={lang} onChange={(e) => change(e.target.value)} className="bg-transparent pr-1 py-1 font-medium cursor-pointer focus:outline-none" data-testid="language-switcher-select">
        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
}

function useCounts(user) {
  const [c, setC] = useState({});
  const loc = useLocation();
  useEffect(() => { if (user) api.get("/me/counts").then((r) => setC(r.data)).catch(() => {}); }, [user, loc.pathname]);
  return c;
}

const Badge = ({ n }) => n > 0 ? <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">{n > 99 ? "99+" : n}</span> : null;

export function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const counts = useCounts(user);
  const links = [["/search", t("nav.discover")], ["/communities", t("nav.communities")], ["/groups", t("nav.groups")], ["/learn", t("nav.learn")]];
  if (user?.role === "employer" || user?.role === "admin") links.push(["/employer", t("nav.workspace")]);
  const cls = ({ isActive }) => `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`;
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
          <img src={LOGO} alt="PrideFocus.world logo" className="h-10 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1 ml-2" aria-label="Main">{links.map(([to, l]) => <NavLink key={to} to={to} className={cls} data-testid={`nav-${to.slice(1)}`}>{l}</NavLink>)}</nav>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangSwitcher /></div>
          {user ? (
            <>
              <Link to="/messages" className="relative p-2 rounded-full hover:bg-slate-100" aria-label={t("nav.messages")} data-testid="header-messages-link"><MessageSquare size={20} /><Badge n={(counts.messages || 0) + (counts.contact_requests || 0)} /></Link>
              <Link to="/notifications" className="relative p-2 rounded-full hover:bg-slate-100" aria-label={t("nav.notifications")} data-testid="header-notifications-link"><Bell size={20} /><Badge n={counts.notifications} /></Link>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 rounded-full p-0.5 hover:bg-slate-100" data-testid="user-menu-trigger">
                  <Avatar src={user.profile?.photo_url} name={user.profile?.display_name} size={34} /><ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.profile?.display_name}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">{t("nav.dashboard")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav(`/u/${user.profile?.username}`)} data-testid="menu-profile">{t("nav.profile")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/profile/edit")} data-testid="menu-edit-profile">{t("nav.edit")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/invitations")} data-testid="menu-invitations">{t("nav.invitations")}</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">{t("nav.admin")}</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">{t("nav.settings")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-signout">{t("nav.signout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex btn-ghost btn-sm" data-testid="header-signin-btn">{t("nav.signin")}</Link>
              <Link to="/register" className="btn-accent btn-sm" data-testid="header-join-btn">{t("nav.join")}</Link>
            </>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t bg-white px-4 py-3 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map(([to, l]) => <NavLink key={to} to={to} className={cls} onClick={() => setOpen(false)}>{l}</NavLink>)}
          <div className="pt-2"><LangSwitcher /></div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  const links = [["/about", "footer.about"], ["/contact", "footer.contact"], ["/help", "footer.help"], ["/guidelines", "footer.guidelines"], ["/privacy", "footer.privacy"], ["/terms", "footer.terms"], ["/report-abuse", "footer.report"]];
  return (
    <footer className="bg-slate-900 text-slate-300 mt-24">
      <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-10">
        <div>
          <div className="bg-white rounded-2xl p-3 inline-block"><img src={LOGO} alt="PrideFocus.world" className="h-14" /></div>
          <p className="mt-4 text-sm max-w-xs">{t("tagline2")}</p>
        </div>
        <nav className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" aria-label="Footer">
          {links.map(([to, k]) => <Link key={to} to={to} className="hover:text-orange-400 transition-colors" data-testid={`footer-${to.slice(1)}`}>{t(k)}</Link>)}
        </nav>
      </div>
      <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} PrideFocus.world · {t("tagline")}</div>
    </footer>
  );
}
